import { Button, DialogTitle, Table, TableBody, TableCell, TableCellLayout, TableHeader, TableHeaderCell, TableRow, Text, Divider, useId, makeStyles, createTableColumn, TableColumnDefinition, TableRowId, useTableFeatures, useTableSelection, TableSelectionCell, tokens } from "@fluentui/react-components";
import { useDeviceDialogStore } from "../../Devices/store/DeviceDialogStore";
import React from "react";
import { useDevicesContext } from "../../Devices/context/DevicesContext";
import { useUsersContext } from "../../../contexts/UserContext";
import { usePostUserAssignmentMutation } from "../api/Create";
import { useUtilitiesContext } from "../../../contexts/UtilitiesContext";
import BaseUserCombobox from "../../../components/BaseUserCombobox";
import { useDeviceGridStore } from "../../Devices/store/DeviceGridStore";
import OverlaySpinner from "../../../components/OverlaySpinner";

const useStyles = makeStyles({
    assignRow: {
        display: 'flex',
        flexDirection: 'row',
        gap: '10px',
        marginTop: '10px',
        alignItems: 'center',
    },
    assignLabel: {
        whiteSpace: 'nowrap',
        flexShrink: 0,
        fontWeight: 'bold',
    },
    tableWrapper: {
        maxHeight: '250px',
        overflowY: 'auto',
    },
    deviceTable: {
        minWidth: '510px',
    },
    actionButtons: {
        display: 'flex',
        justifyContent: 'flex-end',
        marginTop: '10px',
        gap: '10px',
    },
    tableHeaderRow: {
        position: "sticky",
        top: 0,
        backgroundColor: tokens.colorNeutralBackground1,
        zIndex: 2,
    },
});

type Item = {
    id: number;
    type: number;
    label: string;
    status: number;
};

const columns: TableColumnDefinition<Item>[] = [
    createTableColumn<Item>({
      columnId: "type",
    }),
    createTableColumn<Item>({
      columnId: "label",
    }),
    createTableColumn<Item>({
      columnId: "status",
    }),
];

export default function AssignmentInfo() {
    const comboboxId = useId("combobox");
    const styles = useStyles();

    const { listOptions, refetchDevices, } = useDevicesContext();
    const { users } = useUsersContext();
    const { selectedDevices } = useDeviceGridStore();
    const { close } = useDeviceDialogStore();

    const { showNotification } = useUtilitiesContext();
    const [userId, setUserId] = React.useState<string>("");
    const [selectedRows, setSelectedRows] = React.useState(
            () => new Set<TableRowId>()
    );
    const [items, setItems] = React.useState<Item[]>([]);

    const {
        getRows,
        selection: {
            allRowsSelected,
            someRowsSelected,
            toggleAllRows,
            toggleRow,
            isRowSelected,
        },
        } = useTableFeatures(
        {
            columns,
            items,
            getRowId: (item) => item.id,
        },
        [
            useTableSelection({
                selectionMode: "multiselect",
                onSelectionChange: (e, data) => setSelectedRows(data.selectedItems),
            }),
        ]
    );

    const rows = getRows((row) => {
        const selected = isRowSelected(row.rowId);
        return {
            ...row,
            onClick: (e: React.MouseEvent) => toggleRow(e, row.item.id),
            selected,
            appearance: selected ? "brand" : "none",
        };
    });

    const getOptionName = React.useCallback((optionId: number, categoryId: number): string => {
        const list = listOptions?.find(o => o.categoryId === categoryId);
        if (list) {
            const option = list.options.find(o => o.id === optionId);
            return option?.name ?? "N/A";
        }
        return "N/A";
    }, [listOptions]);

    const onError = (error: Error) => {
        showNotification({
            type: "error",
            title: "Error",
            message: error.message,
        });
    };

    const postUserAssignmentMutation = usePostUserAssignmentMutation(
        () => {
            showNotification({
                type: "success",
                title: "Created",
                message: <Text>Assign devices for user successfully.</Text>,
            });
            close();
            refetchDevices();
        },
        onError
    );

    const onSubmit = () => {

        if (selectedRows.size < 1) {
            showNotification({
                type: "error",
                title: "Error",
                message: "No devices selected.",
            });
            return;
        }

        const selectedDeviceIds = Array.from(selectedRows).filter(
            (id): id is number => typeof id === "number"
        );

        postUserAssignmentMutation.mutate({
            userId: userId,
            deviceIds: selectedDeviceIds,
        });
    };

    React.useEffect(() => {
        if (selectedDevices.length > 0) {
            setItems(selectedDevices);
        }
    }, [selectedDevices]);
    
    React.useEffect(() => {
        if (items.length > 0) {
            toggleAllRows({} as React.SyntheticEvent);
        }
    }, [items, toggleAllRows]);

    return (
        <>
            <OverlaySpinner open={postUserAssignmentMutation.isPending} label={"Assigning...."} />

            <DialogTitle>Devices Assignment</DialogTitle>
            <div className={styles.assignRow}>
                <div className={styles.assignLabel}>Assign To:</div>
                <BaseUserCombobox
                    id={comboboxId}
                    data={users}
                    defaultValue={""}
                    disabled={false}
                    onChange={setUserId}
                />
            </div>
            <Divider appearance="strong" />
            <div className={styles.tableWrapper}>
                <Table arial-label="Default table" className={styles.deviceTable}>
                    <TableHeader className={styles.tableHeaderRow}>
                        <TableRow>
                            <TableSelectionCell
                                checked={
                                    allRowsSelected ? true : someRowsSelected ? "mixed" : false
                                }
                                onClick={toggleAllRows}
                            />
                            <TableHeaderCell><b>Type</b></TableHeaderCell>
                            <TableHeaderCell><b>Label</b></TableHeaderCell>
                            <TableHeaderCell><b>Status</b></TableHeaderCell>
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {/* {deviceSelectedSnapshot.map((item) => ( */}
                        {rows.map(({ item, selected, onClick, appearance }) => (
                            <TableRow key={item.id} onClick={onClick}>
                                <TableSelectionCell checked={selected} />                                
                                <TableCell>
                                    <TableCellLayout>
                                        {getOptionName(item.type, 2)}
                                    </TableCellLayout>
                                </TableCell>
                                <TableCell>
                                    <TableCellLayout>
                                        {item.label}
                                    </TableCellLayout>
                                </TableCell>
                                <TableCell>
                                    <TableCellLayout>
                                        {getOptionName(item.status, 9)}
                                    </TableCellLayout>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </div>
            <div className={styles.actionButtons}>
                <Button onClick={close} appearance="secondary">Close</Button>
                <Button onClick={onSubmit} appearance="primary" disabled={selectedRows.size < 1 || !userId}>Assign</Button>
            </div>
        </>
    )
}